import React, { useEffect } from 'react';
import { Check, X, Target, Layers, Users, Clock, AlertTriangle, Zap } from 'lucide-react';

// Consulting proposal for Dilsher Singh, NOMH Textiles. Call 2026-09-18.
// Rs 10,000 one-time strategy consult, deliberately NOT the done-for-you
// retainer because he already runs an in-house social team.

const Eyebrow = ({ children }) => (
    <div className="text-indigo-300/70 text-[10px] sm:text-xs font-bold tracking-[0.3em] uppercase mb-5">{children}</div>
);

const INCLUDED = [
    ['Full audit of your page', 'Every post read against who you are actually trying to reach, not against view counts.'],
    ['Positioning direction', 'The one line the brand leads with, and the words to stop using. This is the bulk of the value and it outlives the content.'],
    ['Content strategy and pillars', 'The lanes you post in and why each exists. Brand level, so it holds as long as the brand does.'],
    ['Research and ideation framework', 'How to decide what to make, so your team stops guessing at the algorithm.'],
    ['Scripting framework', 'How to write them. Not a stack of templates, the method for building your own.'],
    ['Our templates, as they stand', 'Handed over in full, plus how to adapt them rather than copy them.'],
    ['One call of 1 to 2 hours', 'Your social team on it with you. That call is where the whole consult is delivered.'],
];

const EXCLUDED = [
    'Writing, filming, editing or posting your content',
    'Managing your page or your team',
    'Paid ads, boosting or media buying',
    'Ongoing retainer support after the call',
];

export default function NomhConsult() {
    useEffect(() => {
        document.title = 'SocialSEO | NOMH Textiles';
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className="font-sans leading-relaxed text-white overflow-x-hidden selection:bg-indigo-500/30 pb-24 pt-24 relative z-10 min-h-screen">
            <div className="fixed inset-0 min-h-screen bg-[#080B11] -z-20 overflow-hidden">
                <div className="absolute top-[-20%] left-[-10%] w-[60%] h-[60%] bg-indigo-600/20 blur-[160px] rounded-full mix-blend-screen pointer-events-none" />
                <div className="absolute bottom-[-20%] right-[-10%] w-[60%] h-[60%] bg-emerald-600/15 blur-[160px] rounded-full mix-blend-screen pointer-events-none" />
            </div>

            <header className="fixed top-0 left-0 right-0 z-50 p-5 bg-[#07090E]/80 backdrop-blur-lg border-b border-white/5">
                <div className="flex items-center gap-2 w-full max-w-3xl mx-auto">
                    <span className="font-bold tracking-tight text-white text-lg">Social<span className="text-indigo-400">SEO</span></span>
                    <span className="text-white/20 mx-1">/</span>
                    <span className="text-white/70 font-medium text-sm truncate">NOMH Textiles</span>
                </div>
            </header>

            <main className="max-w-3xl mx-auto px-5 pt-8 space-y-16">

                {/* Hero */}
                <section>
                    <Eyebrow>Consulting proposal · 18 Sep 2026</Eyebrow>
                    <h1 className="text-4xl sm:text-5xl font-black tracking-tight mb-6 bg-clip-text text-transparent bg-gradient-to-r from-white via-indigo-200 to-indigo-400 leading-[1.05] pb-1">
                        Premium is not<br />your pitch.
                    </h1>
                    <p className="text-white/70 text-base sm:text-lg leading-relaxed mb-4">
                        Hey Dilsher, the fastest thing we can change is the word you lead with.
                    </p>
                    <p className="text-slate-400 text-sm sm:text-base leading-relaxed">
                        Premium tells a founder this will cost more. It does not tell them why to pick you, and it invites the
                        exact discount conversation you said you do not want. Everything you described that actually wins the
                        client is something else: transparency, clear communication, account managers on the file, wide variety
                        at low quantities so a first collection cannot sink them. That is not premium. That is safety.
                    </p>
                </section>

                {/* The read */}
                <section>
                    <Eyebrow>What we heard</Eyebrow>
                    <div className="space-y-3">
                        <div className="rounded-2xl border border-emerald-500/25 bg-emerald-500/[0.07] p-5 sm:p-6">
                            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-300 mb-2.5">The asset you undersell</div>
                            <p className="text-slate-200 text-sm leading-relaxed">
                                Wide variety of styles at low quantities, then scale what works. For a founder launching a
                                clothing brand, that is the difference between one expensive bet and a survivable experiment.
                                Most manufacturers make them guess big. You do not, and almost nothing on your page says so.
                            </p>
                        </div>
                        <div className="rounded-2xl border border-white/10 bg-white/5 p-5 sm:p-6">
                            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-indigo-300/80 mb-2.5">The bottleneck you named</div>
                            <p className="text-slate-300 text-sm leading-relaxed">
                                You said you cannot tell what the algorithm will pick up. Our answer is that you should stop
                                trying. At a 3 to 5 lakh ticket and 20 to 25 lakh lifetime value, you do not need reach, you need
                                the right forty people to see you. Those are different content strategies, and one of them is
                                much easier to run.
                            </p>
                        </div>
                        <div className="rounded-2xl border border-amber-500/25 bg-amber-500/[0.07] p-5 sm:p-6">
                            <div className="flex items-start gap-3.5">
                                <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
                                <div>
                                    <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-amber-300 mb-2">Worth saying plainly</div>
                                    <p className="text-slate-300 text-sm leading-relaxed">
                                        You want to go from 5 to 10 clients a month to 80 to 100. At your ticket size that is
                                        three to five crore of new business a month. Content can move you a long way up that
                                        curve, and it cannot do all of it on its own. The harder question is whether the factory
                                        floor can take 100 new clients a month, and that is a capacity problem rather than a
                                        marketing one. We will tell you where content stops and where the constraint moves.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Why consulting */}
                <section>
                    <Eyebrow>Why this and not the retainer</Eyebrow>
                    <div className="rounded-2xl border border-white/10 bg-white/5 p-6 sm:p-7 flex items-start gap-4">
                        <div className="shrink-0 w-10 h-10 rounded-xl bg-indigo-500/15 text-indigo-400 flex items-center justify-center">
                            <Users className="w-5 h-5" />
                        </div>
                        <div>
                            <h3 className="font-bold text-white text-[15px] sm:text-base mb-2">You already have the team. You are missing the direction.</h3>
                            <p className="text-slate-400 text-sm leading-relaxed">
                                Our main offer is done for you, and we are not recommending it to you. When a company already runs
                                an in-house social team, outsourcing usually ends with them rebuilding in-house anyway. You are
                                past that step. What your team is short of is a positioning to work from and a method for deciding
                                what to make, which is exactly what this consult hands them.
                            </p>
                        </div>
                    </div>
                </section>

                {/* Scope */}
                <section>
                    <Eyebrow>What you get</Eyebrow>
                    <div className="space-y-2.5 mb-6">
                        {INCLUDED.map(([h, s]) => (
                            <div key={h} className="rounded-xl border border-white/10 bg-white/5 p-4 flex items-start gap-3.5">
                                <Check className="w-4 h-4 text-emerald-400 shrink-0 mt-1" />
                                <div className="min-w-0">
                                    <h3 className="font-bold text-white text-[15px] mb-1">{h}</h3>
                                    <p className="text-slate-400 text-sm leading-relaxed">{s}</p>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="rounded-2xl border border-white/10 bg-black/20 p-5 sm:p-6">
                        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-rose-300/80 mb-3">Not included, so there is no confusion later</div>
                        <div className="space-y-2">
                            {EXCLUDED.map((x) => (
                                <div key={x} className="flex gap-3 text-slate-400 text-sm leading-relaxed">
                                    <X className="w-4 h-4 text-rose-400/70 shrink-0 mt-0.5" />
                                    <span>{x}</span>
                                </div>
                            ))}
                        </div>
                        <p className="text-slate-500 text-xs leading-relaxed mt-4">
                            If you want help running the systems afterwards, that is a separate conversation and a separate
                            number. This proposal does not commit either of us to it.
                        </p>
                    </div>
                </section>

                {/* Process */}
                <section>
                    <Eyebrow>How it runs</Eyebrow>
                    <div className="space-y-2.5">
                        {[
                            ['01', 'You sign and clear the invoice', 'Bank details are on the invoice. Net banking, as you asked.'],
                            ['02', 'We send the questionnaire', 'Around twenty minutes. Fill it yourself rather than handing it to the team, since the positioning has to come from you.'],
                            ['03', 'We research, 2 to 3 working days', 'Your page, your competitors, and the accounts your buyers actually follow.'],
                            ['04', 'The call, 1 to 2 hours', 'You and your social team. We walk the positioning, the pillars and the frameworks, and your team leaves able to run them.'],
                        ].map(([n, h, s]) => (
                            <div key={n} className="flex items-start gap-5 rounded-2xl border border-white/10 bg-white/5 p-5">
                                <div className="text-xl font-black text-indigo-400/80 shrink-0 tabular-nums">{n}</div>
                                <div className="min-w-0">
                                    <h3 className="font-bold text-white text-[15px] mb-1">{h}</h3>
                                    <p className="text-slate-400 text-sm leading-relaxed">{s}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* Price */}
                <section>
                    <div className="rounded-3xl border border-white/10 bg-gradient-to-br from-slate-900 to-[#0F172A] p-7 sm:p-9 text-center">
                        <div className="flex flex-wrap items-baseline justify-center gap-x-3 gap-y-1 mb-4">
                            <div className="text-4xl sm:text-5xl font-black tracking-tight text-white">₹10,000</div>
                            <div className="text-white/50 text-sm font-semibold">one time, plus GST</div>
                        </div>
                        <p className="text-slate-400 text-sm leading-relaxed max-w-md mx-auto mb-6">
                            One payment. No retainer, no lock in, nothing recurring. For context, it is a fraction of one order
                            at your average ticket.
                        </p>
                        <div className="inline-flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-white/40">
                            <span className="inline-flex items-center gap-1.5"><Target className="w-3.5 h-3.5" /> Positioning</span>
                            <span className="inline-flex items-center gap-1.5"><Layers className="w-3.5 h-3.5" /> Frameworks</span>
                            <span className="inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> 1 to 2 hour call</span>
                        </div>
                    </div>
                </section>

                {/* Next */}
                <section>
                    <div className="rounded-2xl border border-white/10 bg-white/5 p-7 text-center">
                        <Zap className="w-7 h-7 text-indigo-400 mx-auto mb-4" />
                        <h2 className="text-xl sm:text-2xl font-black tracking-tight text-white mb-3">
                            Send us your GSTIN and registered address.
                        </h2>
                        <p className="text-slate-400 text-sm leading-relaxed max-w-md mx-auto">
                            That is all we need to raise the invoice and the agreement together. Questionnaire follows the same
                            day, and we can hold the call this week.
                        </p>
                    </div>
                </section>

            </main>
        </div>
    );
}
